import React, { createContext, useContext, useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "./AuthContext";

const CartContext = createContext();

export { CartContext };

export const CartProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return undefined;

    const loadCart = async () => {
      setLoading(true);
      try {
        const savedCart = localStorage.getItem("salene_cart");
        const localCart = savedCart ? JSON.parse(savedCart) : [];

        if (!user?.id) {
          setCart(Array.isArray(localCart) ? localCart : []);
          return;
        }

        const cartRef = doc(db, "users", user.id, "cart", "current");
        const snapshot = await getDoc(cartRef);
        const remoteCart = snapshot.exists() ? snapshot.data().items : null;
        const nextCart = Array.isArray(remoteCart) ? remoteCart : localCart;
        setCart(Array.isArray(nextCart) ? nextCart : []);
        localStorage.removeItem("salene_cart");
      } catch (err) {
        console.error("Failed to load saved cart:", err);
        setCart([]);
      } finally {
        setLoading(false);
      }
    };

    loadCart();
  }, [authLoading, user?.id]);

  useEffect(() => {
    if (loading || authLoading) return;

    if (user?.id) {
      const cartRef = doc(db, "users", user.id, "cart", "current");
      setDoc(cartRef, { items: cart, updatedAt: new Date().toISOString() }).catch((err) => {
        console.error("Failed to save cart:", err);
      });
    } else {
      localStorage.setItem("salene_cart", JSON.stringify(cart));
    }
  }, [authLoading, cart, loading, user?.id]);

  const addToCart = (product, quantity = 1, variant = "100 ML") => {
    setCart((prev) => {
      const existingItem = prev.find((item) => item.id === product.id && item.variant === variant);
      if (existingItem) {
        return prev.map((item) =>
          item.id === product.id && item.variant === variant
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [
        ...prev,
        {
          id: product.id,
          name: product.name,
          price: product.price,
          variant,
          quantity,
          image: product.images?.[0] || product.imageUrl,
        },
      ];
    });
  };

  const removeFromCart = (id, variant) => {
    setCart((prev) => prev.filter((item) => !(item.id === id && item.variant === variant)));
  };

  const updateQuantity = (id, variant, quantity) => {
    if (quantity <= 0) {
      removeFromCart(id, variant);
      return;
    }
    setCart((prev) =>
      prev.map((item) =>
        item.id === id && item.variant === variant ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const value = {
    cart,
    loading,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalPrice,
    getTotalItems,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
